import { ChangeEvent, useEffect, useState } from "react";
import JicFloatingWidget from "./jicFloatingWidget";
import { describeFace, loadFaceRecognitionModels } from "../lib/faceRecognition";
import { getJicToken, getJicUser, loginJic, onJicChange, type JicUser } from "../lib/jic";

// /create — 锚点创世台:任何登录 JIC 的人花 LOVE 铸造中转站/预注册锚点。
// MVP 语义(Router 合约前):燃烧 1 LOVE + 池内创世资金 ≥ 9 LOVE = 白皮书 90/5/5。
const JIC_URL = "https://u.jic.io";
const DRAFT_KEY = "face3:genesis-draft";

type Terms = {
  burn: { amount: number; to: string; token: string };
  pool: { minLove: number };
};

type Draft = {
  slug: string;
  label: string;
  kind: "relay" | "prereg";
  poolAddress: string;
  note: string;
  txHash?: string;
};

const emptyDraft: Draft = { slug: "", label: "", kind: "relay", poolAddress: "", note: "" };

export default function AnchorGenesis() {
  const [user, setUser] = useState<JicUser | null>(() => getJicUser());
  const [terms, setTerms] = useState<Terms | null>(null);
  const [draft, setDraft] = useState<Draft>(() => {
    try {
      return { ...emptyDraft, ...(JSON.parse(sessionStorage.getItem(DRAFT_KEY) || "{}") as Partial<Draft>) };
    } catch {
      return emptyDraft;
    }
  });
  const [phase, setPhase] = useState<"form" | "minting" | "minted">("form");
  const [status, setStatus] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [templates, setTemplates] = useState(0);
  const [enrolling, setEnrolling] = useState(false);

  useEffect(() => onJicChange(setUser), []);

  useEffect(() => {
    fetch(`${JIC_URL}/v1/face/anchor/genesis/terms`)
      .then((r) => r.json())
      .then(setTerms)
      .catch(() => undefined);
  }, []);

  const saveDraft = (next: Draft) => {
    setDraft(next);
    try {
      sessionStorage.setItem(DRAFT_KEY, JSON.stringify(next));
    } catch { /* 忽略 */ }
  };

  // 指挥舱燃烧支付回跳:#love_tx=<hash> → 自动提交创世
  useEffect(() => {
    const match = window.location.hash.match(/^#love_tx=(0x[0-9a-fA-F]{64})$/);
    if (!match) return;
    window.history.replaceState(null, "", window.location.pathname);
    const next = { ...draft, txHash: match[1] };
    saveDraft(next);
    void submitGenesis(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const valid =
    /^[a-z0-9_-]{1,32}$/.test(draft.slug) &&
    draft.label.trim().length > 0 &&
    /^0x[a-fA-F0-9]{40}$/.test(draft.poolAddress);

  const burnViaCabin = () => {
    if (!terms || !valid) {
      setStatus("请先填写完整:slug(小写字母数字)、名称、池地址(0x)");
      return;
    }
    if (!getJicToken()) {
      loginJic({ clearLocal: false });
      return;
    }
    const params = new URLSearchParams({
      pay_to: terms.burn.to,
      pay_amount: String(terms.burn.amount),
      pay_token: "LOVE",
      pay_note: `创世燃烧 · ${draft.slug}`,
      pay_return: window.location.href.split("#")[0],
    });
    window.location.href = `https://u.jic.io/u?${params.toString()}`;
  };

  const submitGenesis = async (payload: Draft) => {
    const token = getJicToken();
    if (!token) {
      setStatus("请先登录 JIC 再提交创世");
      return;
    }
    setPhase("minting");
    setStatus("燃烧交易已确认，正在链上验证并铸造锚点…");
    try {
      const response = await fetch(`${JIC_URL}/v1/face/anchor/genesis`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const data = (await response.json()) as { ok?: boolean; error?: string; hint?: string; balance?: string };
      if (!data.ok) {
        setPhase("form");
        setStatus(
          data.error === "pool underfunded"
            ? `池内创世资金不足:需要 ≥ ${terms?.pool.minLove ?? 9} LOVE,当前 ${Number(data.balance || 0).toFixed(5)}。先给池地址转够 LOVE 再重试。`
            : data.error === "slug taken" || data.error === "slug owned on-chain"
            ? `「${payload.slug}」已被占用,换一个 slug`
            : `${data.error || "创世失败"}${data.hint ? ` · ${data.hint}` : ""}`
        );
        return;
      }
      setPhase("minted");
      setStatus("锚点已铸造 ✓ 现在上传识别模板(至少一张清晰面容图)");
      try {
        sessionStorage.removeItem(DRAFT_KEY);
      } catch { /* 忽略 */ }
    } catch {
      setPhase("form");
      setStatus("网络异常,燃烧交易未作废,稍后重试提交即可(txHash 已保存)");
    }
  };

  const enrollTemplate = async () => {
    const token = getJicToken();
    if (!token || !imageUrl.trim()) return;
    setEnrolling(true);
    setStatus("计算 128 维模板…");
    try {
      await loadFaceRecognitionModels();
      const image = new Image();
      image.crossOrigin = "anonymous";
      await new Promise<void>((resolve, reject) => {
        image.onload = () => resolve();
        image.onerror = () => reject(new Error("图片加载失败(URL 或 CORS 问题,可用 Wikimedia 直链)"));
        image.src = imageUrl.trim();
      });
      const descriptor = await describeFace(image);
      if (!descriptor) throw new Error("图中没有清晰面容");
      const response = await fetch(`${JIC_URL}/v1/face/anchor/${draft.slug}/descriptor`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ descriptor }),
      });
      const data = (await response.json()) as { ok?: boolean; error?: string; anchor?: { templates: number } };
      if (!data.ok) throw new Error(data.error || "模板入库失败");
      setTemplates(data.anchor?.templates || templates + 1);
      setImageUrl("");
      setStatus(`模板已入库 ✓ 共 ${data.anchor?.templates ?? templates + 1} 张,可继续追加`);
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "模板入库失败");
    } finally {
      setEnrolling(false);
    }
  };

  const field = (key: keyof Draft, placeholder: string, hint?: string) => (
    <label style={{ display: "block", marginBottom: 14 }}>
      <small style={{ display: "block", marginBottom: 5, color: "#7f96a3", fontSize: 11, letterSpacing: ".08em" }}>{hint}</small>
      <input
        value={String(draft[key] || "")}
        onChange={(event: ChangeEvent<HTMLInputElement>) => saveDraft({ ...draft, [key]: event.target.value })}
        placeholder={placeholder}
        autoCapitalize="off"
        spellCheck={false}
        style={{ width: "100%", padding: "13px 14px", borderRadius: 12, border: "1px solid rgba(255,255,255,.16)", background: "rgba(255,255,255,.05)", color: "white", fontSize: 14 }}
      />
    </label>
  );

  return (
    <main className="face3-page">
      <div className="face3-aurora face3-aurora-one" />
      <div className="face3-aurora face3-aurora-two" />
      <header className="face3-header">
        <a className="face3-brand" href="/" aria-label="FACE3 home"><span className="face3-mark">⚓</span><span>ANCHOR · GENESIS</span></a>
        <span className="face3-mvp-pill">MINT WITH LOVE</span>
      </header>

      <section style={{ position: "relative", zIndex: 1, maxWidth: 640, margin: "30px auto 60px" }}>
        <p className="face3-eyebrow">GENESIS</p>
        <h1 style={{ margin: "6px 0 10px", fontSize: "clamp(30px,5vw,44px)", letterSpacing: "-.04em" }}>用 LOVE 铸造一个锚点</h1>
        <p style={{ color: "#9ab0bd", fontSize: 13, lineHeight: 1.8 }}>
          中转站(圣像/歌曲/符号,属于所有人)或预注册(为品牌/偶像占位,正主活体即归位)。
          创世 = 池内注入 ≥ {terms?.pool.minLove ?? 9} LOVE 创世资金 + 燃烧 {terms?.burn.amount ?? 1} LOVE。
          你即策展人:命名、模板、激励池规则归你。
        </p>

        {phase !== "minted" && (
          <div style={{ marginTop: 24, padding: 22, border: "1px solid rgba(255,255,255,.12)", borderRadius: 20, background: "rgba(7,16,24,.72)" }}>
            {field("slug", "jesus / love749 / mybrand…", "SLUG · 唯一标识,jic.io/<slug> 短链(小写字母/数字/_/-)")}
            {field("label", "XX · LOVE 中转站", "名称 · 扫描命中时展示")}
            <label style={{ display: "block", marginBottom: 14 }}>
              <small style={{ display: "block", marginBottom: 5, color: "#7f96a3", fontSize: 11, letterSpacing: ".08em" }}>形态</small>
              <div style={{ display: "flex", gap: 8 }}>
                {(["relay", "prereg"] as const).map((k) => (
                  <button key={k} type="button" onClick={() => saveDraft({ ...draft, kind: k })}
                    style={{ flex: 1, padding: "12px 0", borderRadius: 12, cursor: "pointer", fontWeight: 800, fontSize: 13,
                      border: draft.kind === k ? "1px solid rgba(110,241,216,.6)" : "1px solid rgba(255,255,255,.14)",
                      background: draft.kind === k ? "rgba(110,241,216,.12)" : "rgba(255,255,255,.04)", color: draft.kind === k ? "#6ef1d8" : "#c7d6de" }}>
                    {k === "relay" ? "⛪ 中转站" : "◇ 预注册"}
                  </button>
                ))}
              </div>
            </label>
            {field("poolAddress", "0x…", `公共池地址 · 你控制的 0x,创世前先转入 ≥ ${terms?.pool.minLove ?? 9} LOVE`)}
            {field("note", "一句话说明(可选)", "说明")}
            <button type="button" className="face3-primary-button" onClick={burnViaCabin} disabled={phase === "minting"}>
              {phase === "minting" ? "铸造中…" : user && getJicToken() ? `🔥 指挥舱燃烧 ${terms?.burn.amount ?? 1} LOVE 并铸造` : "登录 JIC 开始创世"}
            </button>
            {draft.txHash && phase === "form" && (
              <button type="button" className="face3-primary-button" style={{ marginTop: 8, filter: "saturate(.5)" }} onClick={() => submitGenesis(draft)}>
                已有燃烧交易,重试提交创世
              </button>
            )}
          </div>
        )}

        {phase === "minted" && (
          <div style={{ marginTop: 24, padding: 22, border: "1px solid rgba(110,241,216,.35)", borderRadius: 20, background: "rgba(9,26,24,.85)" }}>
            <h2 style={{ margin: "0 0 6px", fontSize: 20 }}>⚓ @{draft.slug} 已铸造</h2>
            <p style={{ color: "#8fd8c4", fontSize: 12 }}>短链 jic.io/{draft.slug} 已生效 · 现在录入识别模板(支持多张,建议 3 张以上不同角度/时代的图)</p>
            <input
              value={imageUrl}
              onChange={(event) => setImageUrl(event.target.value)}
              placeholder="https://…jpg(需支持 CORS,如 Wikimedia 直链)"
              style={{ width: "100%", margin: "10px 0", padding: "13px 14px", borderRadius: 12, border: "1px solid rgba(255,255,255,.16)", background: "rgba(255,255,255,.05)", color: "white", fontSize: 13 }}
            />
            <button type="button" className="face3-primary-button" onClick={enrollTemplate} disabled={enrolling || !imageUrl.trim()}>
              {enrolling ? "入库中…" : `录入模板(已有 ${templates} 张)`}
            </button>
            {templates > 0 && (
              <a href={`/anchor/${draft.slug}`} style={{ display: "block", marginTop: 14, color: "#58e8ff", fontSize: 14, fontWeight: 800 }}>
                打开 @{draft.slug} 的锚点站 →
              </a>
            )}
          </div>
        )}

        {status && <p style={{ marginTop: 14, color: "#9ab0bd", fontSize: 13 }}>{status}</p>}

        <p style={{ marginTop: 26, color: "#5d7482", fontSize: 11, lineHeight: 1.8 }}>
          红线:在世真人的脸/声只能本人活体认领,预注册他人身份的锚点资金进托管、正主认领即归位(你获 10% 引荐奖励)。
          垃圾/冒认锚点会被举报下架,创世燃烧不退。Router 合约上线后创世与路由分账升级为链上强制。
        </p>
      </section>
      <JicFloatingWidget />
    </main>
  );
}
